<template>
  <div class="container">
    <div class="columns is-centered">
      <div class="column">
        <h1 style="text-align: center;">Frequently Asked Questions (FAQ)</h1>
        <p>
          This FAQ answers basic questions about the Eviction Diversion Program. Please see the <a
            target="_blank"
            href="https://dpd-eviction-diversion-documents.s3.us-east-1.amazonaws.com/EDP+Policies+%26+Procedures+Rev.+11.4.26.pdf"
          ><u>EDP Policies and Procedures</u></a> for more detail on these topics and others.
        </p>
        <p>
          The <a
            target="_blank"
            href="https://dpd-eviction-diversion-documents.s3.us-east-1.amazonaws.com/FAQ+Change+Log+Rev.+10.22.26.pdf"
          ><u>change log</u></a> documents substantive changes made to this FAQ since 2-4-2026.
        </p>
        <p><b><u>CATEGORIES</u></b></p>
        <p><b><a href="#general"><u>General</u></a></b></p>
        <p id="general">
          <b><a href="#tfa"><u>Targeted Financial Assistance</u></a></b>
        </p>
        <p><b><a href="#additionalRequirements"><u>Application Requirements</u></a></b></p>
        <p><b><a href="#mediationQuestions"><u>Mediation</u></a></b></p>
        <p><b><a href="#tenantQuestions"><u>Tenant Questions</u></a></b></p>
        <p>
          <b><u>General Questions</u></b>
        </p>
        <ol>
          <li>
            <p><b>What is the Eviction Diversion Program?</b></p>
            <p>
              EDP is a free City service for residential tenants and landlords. 
              EDP is an opportunity to work out an agreement, receive financial assistance (called “Targeted Financial Assistance” or “TFA”), or both, in order to avoid the costs and burden of an eviction. 
              Depending on the characteristics of a case, EDP participants will receive some combination of mediation, financial assistance, and dispute resolution resources. 
              EDP is initiated through a landlord application; tenants cannot apply to EDP.
            </p>
          </li>
          <br>
          <li>
            <p><b>Who is required to participate in the Eviction Diversion Program?</b></p>
            <p>
              All landlords of residential properties are required to participate in EDP before filing a Landlord-Tenant complaint in Municipal Court or initiating another proceeding to evict a residential tenant.  
            </p>
          </li>
          <br>
          <li>
            <p><b>Where can I find the Eviction Diversion Program law?</b></p>
            <p>
              EDP is codified at Section 9-811 of the Philadelphia Code, which can be accessed at <a
                target="_blank"
                href="https://codelibrary.amlegal.com/codes/philadelphia/latest/philadelphia_pa/0-0-0-278160"
              ><u>https://codelibrary.amlegal.com/codes/philadelphia/latest/philadelphia_pa/0-0-0-278160</u></a>. 
            </p>
          </li>
          <br>
          <li>
            <p><b>How long does the Eviction Diversion Program take?</b></p>
            <p>
              Landlords are required to participate in EDP in reasonable good faith for at least 30 full days. 
              The 30 day period begins on the day after landlord's EDP application is approved. 
              If a landlord has participated in EDP in reasonable good faith, they will receive an Outcome Certificate on the 31st day which states that they have met the participation requirement. 
              Landlords and tenants are encouraged to continue participating in the program beyond the 30 day period to avoid a court filing if they are still pursuing an agreement or Targeted Financial Assistance. 
              Please be aware: a court filing against the tenant by the landlord will result in the disqualification of a pending or future TFA application.
            </p>
          </li>
          <br>
          <li>
            <p><b>How will I know what I need to do during the Eviction Diversion Program?</b></p>
            <p>
              Landlords and tenants will receive text and/or email notifications from EDP using contact information provided by the landlord on the initial application. These notifications will provide instructions about steps that need to be taken.
            </p>
            <p>
              TENANTS ONLY: If you are a tenant and you have not received a notification or are unsure if the program has the correct contact information for you, please call the Eviction Diversion Tenant Hotline at <a href="tel:215-523-9501"><u>215-523-9501</u></a>, Monday - Friday (9 AM - 4 PM). 
            </p>
            <p>
              LANDLORDS ONLY: If you are a landlord and are concerned you are not receiving notifications, please contact <a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a>.
            </p>
          </li>
          <br>
          <li>
            <p><b>How can I know my status in the program?</b></p>
            <p>
              Landlords and tenants may check the status of an EDP matter at any time by visiting <a
                target="_blank"
                href="Status"
              ><u>https://eviction-diversion.phila.gov/Status</u></a> and entering their EDP2 application number. The EDP2 application ID can be found in the text or e-mail notifications you received from the City.
            </p>
            <p>
              Additionally, landlords and tenants will receive text and/or email notifications from EDP using the contact information provided by the landlord on the initial application. These notifications will inform the parties about status changes such as application approval, TFA approval, mediation scheduling, and final outcomes. 
            </p>
          </li>
          <br>
          <li>
            <p><b>The Eviction Diversion Program law requires that landlords participate in “reasonable good faith.” What do I need to do to meet this requirement?</b></p>
            <p>
              The City Ordinance, Philadelphia Code § 9-811, requires that a tenant and landlord have both made reasonable efforts in the EDP for at least 30 days from application approval date to achieve a resolution.
            </p>
            <p>
              For a landlord to meet the definition of good faith participation, the following conditions must be met:
            </p>
            <ol>
              <li>
                Provide evidence that the most recent version Notice of Diversion Rights was sent to the tenant, an updated ledger account of any balances owed, and a list of any additional issues to address through mediation as required, and proof of an active rental license for the dwelling, which may include documentation, if requested.
              </li>
              <li>
                Provide current and accurate contact information for tenant(s) as part of the EDP application.
              </li>
              <li>
                Respond timely to communications from a tenant, a housing counselor, City-funded agency representative, or other program participants during the program, including communications about resolving application or TFA issues and reaching an agreement. Absent extenuating circumstances, timely means within five days.
              </li>
              <li>
                In direct negotiation, respond timely to tenant requests for resolution including payment plans and resolving other issues raised.
              </li>
              <li>
                Attend mediation sessions as scheduled. A landlord may have an agent attend, but the landlord must be available by phone to participate if needed. The agent must have all necessary information and authority to resolve disputes and enter into agreements.
              </li>
              <li>
                During mediation or negotiations, the landlord must make reasonable efforts to address all issues and/or disputes that could lead to an eviction filing.
                <ol type="a">
                  <li>
                    "Reasonable efforts" means responding to each issue or proposal raised by the tenant, including issues other than non-payment such as habitability concerns, and explaining the reason for any decision to decline a request or maintain a position.
                  </li>
                  <li>
                    Categorical refusal to discuss an issue, or failure to explain the basis for a position after receiving tenant input, does not constitute reasonable efforts.
                  </li>
                </ol>
              </li>
            </ol>
            <p>
              Tenants are also expected to meet certain participation responsibilities. These are:
            </p>
            <ol>
              <li>
                Tenant must actively participate by attending mediation sessions as scheduled. If not, a tenant risks non-compliance and the legal process of eviction.
              </li>
              <li>
                Tenant must attempt to address all issues raised.
              </li>
              <li>
                Tenant must submit all requested materials for TFA within five days of notice.
              </li>
            </ol>
          </li>
          <br>
          <li>
            <p><b>How will I know when I have completed the Eviction Diversion Program, and what outcomes will I receive?</b></p>
            <p>
              All landlords and tenants will receive a notification when the 30-day mandatory participation period has concluded. Each EDP application receives two outcomes: an EDP Outcome and a TFA Outcome. The notification will indicate if the landlord has or has not met the requirement of participating in reasonable good faith. Both outcomes must be "Good Faith" to receive an Outcome Certificate at this time. An Outcome Certificate will not be provided if a landlord has not met the good faith requirement.
            </p>
            <p>
              Landlords and tenants are encouraged to continue participating in the program beyond the 30 day period if they are still pursuing an agreement or Targeted Financial Assistance approval. 
            </p>
          </li>
          <br>
          <li>
            <p><b>Who do I contact with questions not addressed in this FAQ?</b></p>
            <p>
              For landlord or tenant questions about an application for Targeted Financial Assistance: the PHDC financial specialist assigned to the application.
            </p>
            <p>
              For landlord questions about other aspects of EDP: Email CORA Good Shepherd Mediation at <a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a>. (Tenants who send emails to this address will be referred to one of the tenant hotlines.)
            </p>
            <p>
              For tenant questions about EDP not related to TFA: Call the EDP Tenant Hotline at <a href="tel:215-523-9501"><u>215-523-9501</u></a>.
            </p>
            <p>
              For general tenant legal advice and possible representation: Call the Philly Tenant Hotline at <a href="tel:267-443-2500"><u>267-443-2500</u></a>.
            </p>
          </li>
          <br>
          <li>
            <p><b>I am required to send my tenant a written notice—such as a “Notice to Quit” or “Lease Termination Notice”—before I can file for eviction in Landlord-Tenant court. Is this notice the same as the Notice of Diversion Rights?</b></p>
            <p>
              No. The Notice of Diversion Rights is a separate notice required by the Eviction Diversion Program. It does not replace any other notice that may be required by law or lease agreement. The Notice of Diversion Rights can be found here: <a
                target="_blank"
                :href="$norDocument"
              ><u id="tfa">Notice of Diversion Rights</u></a>. 
              The Notice of Diversion Rights (NOR) is available in <a :href="$norDocument" target="_blank"><u>English/Spanish</u></a>, <a :href="$norDocumentArabic" target="_blank"><u>English/Arabic</u></a>, <a :href="$norDocumentChinese" target="_blank"><u>English/Chinese (Simplified)</u></a>, <a :href="$norDocumentRussian" target="_blank"><u>English/Russian</u></a>, and <a :href="$norDocumentVietnamese" target="_blank"><u>English/Vietnamese</u></a>.
            </p>
          </li>
          <li>
            <p><b>Can my EDP pathway change during the program?</b></p>
            <p>
              Yes. Pathway changes allow parties to move between different EDP pathways when circumstances warrant. A pathway change has no effect on the landlord's 30-day participation period. Because pathway changes have specific deadlines and requirements, parties should request changes as soon as possible when circumstances change. For more information about pathway changes, please see Section 10 of the <a
                target="_blank"
                href="https://dpd-eviction-diversion-documents.s3.us-east-1.amazonaws.com/EDP+Policies+%26+Procedures+Rev.+2.3.26.pdf"
              ><u>EDP Policies & Procedures</u></a>.
            </p>
          </li>
        </ol>
        <p>
          <b><u>Targeted Financial Assistance Questions</u></b>
        </p>
        <ol>
          <li>
            <p><b>What is Targeted Financial Assistance?</b></p>
            <p>
              Targeted Financial Assistance (TFA) is a one-time payment from the City to a landlord to cover a tenant's past liabilities. So long as a tenant will continue living in the unit for at least two more months, the payment also includes two additional months of rent. In order to receive TFA, a landlord must agree to certain terms as described below.
            </p>
            <p>TFA may also be awarded when the landlord and tenant have agreed through EDP mediation to a "graceful exit," covering arrears and up to two additional months while the tenant remains in the unit before moving out.</p>
          </li>
          <br>
          <li>
            <p><b>How do I apply for Targeted Financial Assistance?</b></p>
            <p>
              Landlords who meet the TFA eligibility criteria will be able apply for TFA through the EDP Application. Landlords must select “Yes” to accepting TFA on the application in order to be considered for TFA. If you are not given the option to apply for TFA on the EDP Application that means that you do not meet the eligibility criteria.
            </p>
          </li>
          <br>
          <li>
            <p><b>What are the eligibility requirements for Targeted Financial Assistance?</b></p>
            <ul>
              <li>
                Tenant arrears are $3,500 or less, as shown on a ledger or rent roll.
              </li>
              <li>
                Tenant income is less than <a
                  target="_blank"
                  href="https://www.phila.gov/departments/division-of-housing-and-community-development/about/income-guidelines/"
                ><u>80% of the Area Median Income (AMI)</u></a>. 
              </li>
              <li>
                The landlord has not commenced an eviction filing for the same tenant and unit within one year of the EDP application submission date.
              </li>
              <li>
                If issues other than non-payment or other qualified expenses exist, the tenant and landlord have negotiated and signed a written agreement resolving all such issues. This agreement can be reached during EDP through mediation or direct negotiation. The agreement can be pursued during EDP at the same time that other aspects of TFA eligibility are being reviewed.
              </li>
              <li>
                TFA may be awarded to pay for a tenant's arrears even if the tenant no longer lives at the property, so long as the tenant can be reached to provide a signed tenant affidavit.
              </li>
            </ul>
          </li>
          <br>
          <li>
            <p><b>What can Targeted Financial Assistance pay for?</b></p>
            <p>
              The portion of the TFA payment designated for arrears may only be applied to rent and other approved expenses owed by the tenant at the time of the EDP application. If the TFA payment includes additional funds, those funds may only be used to pay for rent owed by the tenant for the same unit for months after the last month of arrears claimed by the landlord on the EDP-approved ledger. Parties may check the status page to determine which portion of the TFA payment is designated for arrears and which is designated for additional months of rent.
            </p>
            <p>
              TFA funds may not be used for any other purpose including, but not limited to, security deposits. Legal fees are only eligible for TFA if included in the lease and consistent with current market conditions and the work performed.
            </p>
            <br>
          </li>
          <li>
            <p><b>What terms does a landlord agree to by accepting Targeted Financial Assistance?</b></p>
            <p>
              Landlords who accept TFA should carefully review the <a
                target="_blank"
                :href="$landlordAffidavit"
              ><u>TFA affidavit</u></a> in order to make sure they understand its terms. Among other terms, the landlord agrees that all of the money the tenant owes to the landlord on the date of the EDP application is paid off, that any additional months of rent included in the TFA payment are credited to the tenant's account, and that the landlord will not begin eviction proceedings until after the TFA Protection Period. The prohibition on filing applies to all lease violations and termination of the lease term, not just failure to pay rent.
            </p>
            <p>
              If a tenant makes payments toward arrears before TFA is awarded, the tenant should be credited by the landlord for the amount subsequently covered by TFA.
            </p>
            <br>
          </li>
          <li>
            <p><b>How is the Targeted Financial Assistance Protection Period calculated, and what happens if a landlord files during the Protection Period?</b></p>
            <p>
              The TFA Protection Period is a two-month period during which the landlord may not file for an eviction. This two-month period begins after any forward months for which TFA has covered the rent. The landlord cannot file for eviction until the first day of the month after the Protection Period ends.
            </p>
            <p>
              If a landlord files during the Protection Period, EDP has the right to request return of funds and withdrawal of the court case. The Protection Period remains in place regardless.
            </p>
            <br>
          </li>
          <li>
            <p>
              <b>How long does the Targeted Financial Assistance process take, and what are the deadlines?</b>
            </p>
            <p>
              <u>Step 1 - Initial EDP Application Approval</u>
            </p>
            <p>
              The first step is initial EDP application review by CORA. To start this step, the landlord should submit an EDP application. If the landlord opts into TFA on the initial EDP application, the landlord will submit TFA information and documents at that time. (If the landlord opts into TFA later in the process, the information and documents will be submitted at that point.)
            </p>
            <p>
              <u>Step 2 - TFA Approval</u>
            </p>
            <p>
              The second step is the review of TFA documents and information by PHDC. This step can only start once the initial EDP application has been approved. PHDC will notify the landlord if any corrections are needed. Delays in the submission or resubmission of required materials by the landlord, or of the required materials by the tenant, will delay the PHDC approval process. The landlord and tenant will be notified once PHDC has approved all required information and documents.
            </p>
            <p>
              <u>Step 3 - Payment Processing</u>
            </p>
            <p>
              The final step is review by the third-party payment vendor. If the parties are participating in mediation, this step will only begin once a mediation agreement has been reached or the mediation process has been otherwise closed. When the vendor’s review is complete, the payment will be released, and the parties will be notified.
            </p>
            <p>
              <u>Deadlines</u>
            </p>
            <p>
              The last day for parties to submit TFA materials is 60 days after application approval. PHDC will complete its review within 75 days of application approval.
            </p>
            <br>
          </li>
          <li>
            <p><b>Can I apply for Targeted Financial Assistance more than once?</b></p>
            <p>
              A landlord can only receive TFA once for the same tenant and unit.
            </p>
            <br>
          </li>
          <li>
            <p><b>What expenses are eligible for TFA?</b></p>
            <p>
              The following expenses may be eligible for TFA:
              <ul>
                <li>
                  <b>Utilities:</b> Except for water, utilities are only eligible if unpaid by the tenant and paid by the landlord to the utility provider. Water is eligible if the account is in the tenant's name or if the lease requires the tenant to pay the landlord who then pays the Philadelphia Water Department.
                </li>
                <li>
                  <b>Late Fees:</b> Only eligible if they do not exceed 10% of the monthly rent for each month of late rent, are included in the lease, and (for subsidized tenants) are levied only on the tenant's portion of rent.
                </li>
                <li>
                  <b>Parking, Pet, Rental Insurance, and Returned Payment Fees:</b> Only eligible if included in the lease.
                </li>
                <li>
                  <b>Damage & Repair Costs:</b> Only eligible if the lease states the tenant is responsible for such costs.
                </li>
                <li>
                  <b id="additionalRequirements">Legal Fees:</b> Only eligible if included in the lease and consistent with current market conditions and the work performed.
                </li>
              </ul>
            </p>
            <p>The following expenses are ineligible: security deposits, relocation assistance, and fees for non-law firms to complete EDP applications or process TFA materials.</p>
            <p>PHDC may, in its sole discretion, accept alternate documentation in lieu of a written lease to support the TFA eligibility of the above fees and costs.</p>
            <p>
              For complete details on eligible expenses, please see the Section 7.9 of the <a
                tagret="_blank"
                href="https://dpd-eviction-diversion-documents.s3.us-east-1.amazonaws.com/EDP+Policies+%26+Procedures+Rev.+2.3.26.pdf"
              ><u>EDP Policies & Procedures</u></a>.
            </p>
          </li>
        </ol>
        <p>
          <b><u>Application Requirements </u></b>
        </p>
        <ol>
          <li>
            <p><b>What steps does a landlord need to take to successfully apply for the Eviction Diversion Program?</b></p>
            <ol type="a">
              <li>
                First, the landlord needs to send the current version of the <a
                  target="_blank"
                  :href="$norDocument"
                ><u>Notice of Diversion Rights</u></a> to their tenant and retain proof of mailing or service. Acceptable forms of proof are listed in Question 6 in this section. 
                The Notice of Diversion Rights (NOR) is available in <a :href="$norDocument" target="_blank"><u>English/Spanish</u></a>, <a :href="$norDocumentArabic" target="_blank"><u>English/Arabic</u></a>, <a :href="$norDocumentChinese" target="_blank"><u>English/Chinese (Simplified)</u></a>, <a :href="$norDocumentRussian" target="_blank"><u>English/Russian</u></a>, and <a :href="$norDocumentVietnamese" target="_blank"><u>English/Vietnamese</u></a>.
              </li>
              <li>
                Reach out to the tenant to confirm the tenant's phone number and email address.
              </li>
              <li>
                Second, the landlord should apply to EDP through the web-portal here: <a
                  target="_blank"
                  href="https://eviction-diversion.phila.gov/#"
                ><u>https://eviction-diversion.phila.gov/#</u></a>.
              </li>
              <li>
                Third, until the application is approved, the landlord should watch for communication about any information or documents they may need to correct.
              </li>
            </ol>
            <p>
              If the tenant is a corporate entity that sub-leases to residents, please contact CGS at EvictionDiversionProgram@coraservices.org for special instructions.
            </p>
          </li>
          <br>
          <li>
            <p><b>What are the application approval deadlines and policies?</b></p>
            <p>
              An EDP application must be approved within 30 days of submission. Unapproved applications will expire after 30 days, and landlords will need to reapply.
              Duplicate applications will be denied unless the corresponding active application is withdrawn.
            </p>
          </li>
          <br>
          <li>
            <p><b>Can I withdraw my EDP application?</b></p>
            <p>
              Before an EDP application has been approved, a landlord may withdraw the application for any reason. After an EDP application has been approved, a landlord may only withdraw in limited circumstances. A landlord seeking to withdraw should contact EvictionDiversionProgram@coraservices.org.
            </p>
          </li>
          <br>
          <li>
            <p><b>What should be included in the “Arrears” on the application?</b></p>
            <p>
              Arrears may include all past due rent, fees, utilities, repairs, and legal fees. A utility is only eligible for TFA if the lease obligates the tenant to make the utility payment to the landlord or its agent and the landlord or its agent makes the utility payment to the utility provider. You must upload a ledger with a balance due that equals the arrears on the application.  
            </p>
            <p>
              If arrears are $3,500 or less, you may be eligible for TFA funding. If you want to be considered for TFA funding but the arrears are greater than $3,500, you may agree to waive any amount over $3,500 to be considered for TFA. If you decide to do this, you must indicate the amount forgiven on the required ledger.
            </p>
          </li>
          <br>
          <li>
            <p><b>What documents does a landlord need to apply for the Eviction Diversion Program?</b></p>
            <p>Requirements include:</p>
            <ol>
              <li>
                An active Rental License from the City of Philadelphia.
              </li>
              <li>
                Proof that you sent your tenant the Notice of Diversion Rights. (You can find the current <a
                  target="_blank"
                  :href="$norDocument"
                ><u>Notice of Diversion Rights</u></a> here.) The Notice of Diversion Rights (NOR) is available in <a :href="$norDocument" target="_blank"><u>English/Spanish</u></a>, <a :href="$norDocumentArabic" target="_blank"><u>English/Arabic</u></a>, <a :href="$norDocumentChinese" target="_blank"><u>English/Chinese (Simplified)</u></a>, <a :href="$norDocumentRussian" target="_blank"><u>English/Russian</u></a>, and <a :href="$norDocumentVietnamese" target="_blank"><u>English/Vietnamese</u></a>.
              </li>
              <li>
                A ledger or list of past due amounts detailing what the tenant owes. The document must include the property address, landlord name, tenant name, and dates corresponding with payments made and balances owed. The balance due shown on the ledger must equal the arrears you put on your application. You can find a template here: <a target="_blank" :href="$rentLedgerTemplate"><u>Ledger Template</u></a>.
              </li>
              <li>
                Additional documents will be required for applications seeking TFA. These documents are outlined in answers below.
              </li>
              <li>
                The program may request additional documents, such as a lease, for some applications.
              </li>
            </ol>
            <p>
              Finally, a proposed payment agreement to discuss with the tenant is highly recommended if you are not applying for Targeted Financial Assistance. You can find a template <a
                target="_blank"
                href="https://dpd-eviction-diversion-documents.s3.amazonaws.com/Employee+ID+attribute+update+field.docx"
              ><u>here</u></a>. 
            </p>
          </li>
          <br>
          <li>
            <p><b>What are acceptable forms of proof that I mailed or served the Notice of Diversion Rights?</b></p>
            <p>
              In general, the proof of mailing or service must show WHAT you sent, WHO you sent it to, and WHEN you sent it. Please note, the Notice of Diversion Rights must be sent no more than 30 days before applying to the Eviction Diversion Program.
            </p>
            <p>
              Acceptable proofs of mailing or service include the following. Examples of each method can be found at this  <a
                target="_blank"
                href="https://dpd-eviction-diversion-documents.s3.us-east-1.amazonaws.com/%5BFinal%5D+EDP+FAQ+Tab+-+NOR+Proof+Samples+Document.pdf"
              ><u>link</u></a>.
            </p>
            <ol>
              <li>
                Images of the dated Notice along with a stamped envelope addressed to the tenant.
              </li>
              <li>
                Images of the dated Notice along with a certified mail receipt (green card) showing the tenant's name, address, and the USPS date stamp.
              </li>
              <li>
                A written statement or Affidavit of Service from the landlord attesting that they delivered the “Notice of Diversion Rights” to the tenant. The document must be dated and signed.
              </li>
              <li>
                An image of the dated Notice with the tenant's hand-written or electronic signature.
              </li>
              <li>
                A written statement from the tenant attesting that they received the "Notice of Diversion Rights" from the landlord. This must be signed by the tenant and dated.
              </li>
              <li>
                An image of an email or text to the tenant showing the "Notice of Diversion Rights" as an attachment or copied into the body of the message. The email address or phone number must match the tenant contact information provided on the application and the image must show the date sent.
              </li>
              <li>
                A photo of the dated Notice taped to the tenant's door or mailbox that also shows the tenant's unit or street number.  
              </li>
            </ol>
          </li>
          <br>
          <li>
            <p><b>What is a ledger and what specific information does the program require?</b></p>
            <p>
              The ledger is a document that shows how much the tenant owes and why they owe it. It shows the amounts due for each month, what the tenant paid for each month, and the balance due. The document must include the property address, landlord name, tenant name, and dates for all payments made and balances owed. The balance due shown on the ledger must equal the arrears you put on your application.
            </p>
            <p>
              The following are considered to be acceptable ledgers:
            </p>
            <ol>
              <li>
                A formal ledger or "rent roll" from a property management system. It must include all past-due amounts starting from the time the tenant last had a $0 balance.
              </li>
              <li>
                A Word document, Excel spreadsheet, or handwritten document listing months of rent owed, all payments made during the arrears period, and any other fees. You may use this <a
                  target="_blank"
                  :href="$rentLedgerTemplate"
                ><u>ledger template</u></a>.
              </li>
              <li>
                A Notice to Quit or other official document that shows each month and fee that is past due.
              </li>
            </ol>
            <p>
              Please note that the fillable ledger option is no longer available.
            </p>
          </li>
          <br>
          <li>
            <p><b>What documents does a landlord need to apply for Targeted Financial Assistance?</b></p>
            <p>
              If a landlord wants their application to be considered for TFA funding, the following documents are required, in addition to those listed above:
            </p>
            <ul>
              <li>
                Landlord Affidavit. You must download and sign the <a
                  target="_blank"
                  :href="$landlordAffidavit"
                ><u>landlord affidavit</u></a>. 
              </li>
              <li>
                Bank verification document. Must include the account holder's name, account number, and routing number. Examples: voided check, bank statement, or account detail screenshot.
              </li>
              <li>
                IRS Form W9. Must match the bank account holder's name. If you do not have a copy of your W9, download a fillable copy from the IRS at <a
                  target="_blank"
                  href="https://www.irs.gov/forms-pubs/about-form-w-9"
                ><u>www.irs.gov/forms-pubs/about-form-w-9</u></a> and complete the form. 
              </li>
              <li>
                Proof of property ownership and/or authorization to act on behalf of the property owner. If public records verifying property ownership are not available, you must provide a deed or proof of homeowner's insurance coverage.
              </li>
              <li>
                If an agent or representative is working on behalf of the owner, they will be required to provide a property management agreement, articles of incorporation, and/or attorney authorization sufficient to verify that they have full authority to act on behalf of the owner.
              </li>
            </ul>
            <p>Files should be uploaded separately. For each document, include all pages in a single file.</p>
          </li>
          <br>
          <li>
            <p id="mediationQuestions">
              <b>What if a landlord believes the tenant is a threat to other residents, landlord, or staff?</b>
            </p>
            <p>
              Please see the Philadelphia Code <a
                target="_blank"
                href="https://codelibrary.amlegal.com/codes/philadelphia/latest/philadelphia_pa/0-0-0-278160"
              ><u>Section 9-811(3)</u></a> and consult your legal counsel.
            </p>
            <p>
              If you do not have a lawyer, please visit the BAR LRIS service: <a
                target="_blank"
                href="https://philadelphiabar.org/?pg=LawyerReferralHome"
              ><u>https://philadelphiabar.org/?pg=LawyerReferralHome</u></a>. 
            </p>
            <p>
              You may also seek help through the City of Philadelphia Landlord Gateway: <a
                target="_blank"
                href="https://www.phila.gov/programs/landlord-gateway/"
              ><u>https://www.phila.gov/programs/landlord-gateway/</u></a>. 
            </p>
          </li>
        </ol>
        <p>
          <b><u>Questions about Mediation</u></b>
        </p>
        <ol>
          <li>
            <p><b>What can I expect from the mediation process?</b></p>
            <p>
              During the mediation process, tenants and landlords work together with the assistance of a neutral third party, called a mediator, to resolve disputes. Mediation occurs by phone and sessions are generally one hour or less. The mediator facilitates a discussion of topics including rent, utility payments, repairs, lease terms and, in some cases, graceful exits. The landlord will have an opportunity to share what they are seeking and the tenant and their housing counselor will share the tenant's issues, goals, and available resources. The purpose is to reach a sustainable agreement that works for both sides. Mediation is only successful if both parties participate in good faith.
            </p>
          </li>
          <br>
          <li>
            <p><b>What are common terms agreed to in mediation?</b></p>
            <p>
              Agreements often include regular payments from the tenant and may also include repairs, utility bills, access to the property, or timelines for the tenant leaving the property. In instances where parties work together to bring arrears to $3,500 or under, the landlord may agree to opt into Targeted Financial Assistance. Conditions and eligibility requirements apply.  
            </p>
          </li>
          <br>
          <li>
            <p><b>How do I participate in a mediation session?</b></p>
            <p>
              Mediations are held on weekdays between 9am and 5pm by telephone conference call. You will receive an automated notice with the time and date of your mediation. 48 hours before the mediation you will receive the phone number and five-digit code.
            </p>
            <p>
              If you have multiple mediation sessions, each session will use a different five-digit code. DO NOT reuse a code previously assigned for a completed mediation. If you do not receive a code 24 hours before a continued mediation date, please email <a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a>, call or text <a href="tel:1-445-204-8027"><u>1-445-204-8027</u></a> or reach out to the assigned housing counselor.
            </p>
          </li>
          <li>
            <p><b>Can an agent or attorney attend the mediation for the landlord?</b></p>
            <p>
              Agents or attorneys may represent a landlord at mediation but only if they have settlement authority. Additionally, a landlord who is not present must be accessible for consultation with their representative during the mediation. Lack of settlement authority or necessary access to information needed to negotiate may result in a lack of good faith outcome.
            </p>
          </li>
          <br>
          <li>
            <p><b>What role do housing counselors play in mediations?</b></p>
            <p>
              City-funded housing counselors meet with the tenants before mediation to assess finances and find out if other issues need to be addressed. Housing counselors also attend mediation sessions with the tenant. Landlords are encouraged to communicate with housing counselors.
            </p>
          </li>
          <li>
            <p><b>What happens if an agreement is reached?</b></p>
            <p>
              If the landlord and tenant reach an agreement, the agreement is written down by the mediator, orally signed by parties and provided to both parties by the mediation manager. Both parties should review the agreement and follow the steps determined in mediation. Depending on the terms of the agreement, these steps could include the tenant making regular payments, the landlord making repairs, or adjustments made to property access.
            </p>
            <p>
              If either party detects an error in the agreement document, they should contact EvictionDiversionProgram@coraservices.org within 24 hours (or the next business day) of receipt.
            </p>
          </li>
          <br>
          <li>
            <p><b>What happens if the landlord and tenant do not reach an agreement?</b></p>
            <p>
              If the landlord and tenant do not reach an agreement during mediation, they may ask to continue negotiations and choose a new mediation date. Parties are always encouraged to continue the mediation process, especially if additional information or TFA updates are needed to reach agreement. The tenant should continue to work with the assigned housing counselor if negotiations continue. Regardless of the status of negotiations or TFA, all parties will receive an EDP outcome notification after 30 days have elapsed since approval of the EDP application.
            </p>
          </li>
          <br>
          <li>
            <p><b>How many mediation sessions can I have, and what are the deadlines?</b></p>
            <p>
              Absent extenuating circumstances, no more than three mediations will be scheduled for a single EDP application. All mediations must occur within 60 days of application approval.
            </p>
          </li>
          <br>
          <li>
            <p><b> What should I do if I have concerns about safety or access during the mediation process?</b></p>
            <p>
              Mediators work to uphold the values of safety, access, and inclusion and both parties should feel that they can participate fully in the process.
            </p>
            <p>
              If you have safety or equity concerns while participating in mediation, please email <a href="mailto:evictiondiversionprogram@coraservices.org"><u>evictiondiversionprogram@coraservices.org</u></a> for confidential support. 
            </p>
            <p>
              For interpretation services, please request at the time of application, through the assigned housing counselor or by email prior to mediation at <a href="mailto:evictiondiversionprogram@coraservices.org"><u>evictiondiversionprogram@coraservices.org</u></a>. A mediation session may need to be rescheduled if mediation staff are not made aware of the need for an interpreter before the date of the mediation session. 
            </p>
            <p>
              For support with domestic violence, please contact the Philadelphia Domestic Violence Hotline at <a href="tel:1-866-723-3014"><u>1-866-723-3014</u></a>. 
            </p>
          </li>
          <br>
          <li>
            <p><b>I am not able to attend a scheduled mediation. What should I do?</b></p>
            <p>
              If you are a landlord, contact <a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a> at least 48 hours in advance when possible. Include the reason you are requesting to reschedule and any details about contact with the tenant. Rescheduling requests can typically be honored. However, in order to accommodate the scheduling needs of all parties, a rescheduling request may require that a landlord participate beyond the 30 day mandatory participation period in order to receive a good faith outcome.
            </p>
            <p>
              If you are a tenant contact your housing counselor or Hotline staff at <a href="tel:215-523-9501"><u>215-523-9501</u></a>. 
            </p>
            <p>
              Missed mediation dates may result in documentation that a party did not participate in EDP in good faith and could affect eligibility for Targeted Financial Assistance. 
            </p>
          </li>
          <br>
          <li>
            <p id="tenantQuestions">
              <b>What should I do if all issues have been resolved prior to mediation?</b>
            </p>
            <p>
              Landlords and tenants may reach agreements without the use of a housing counselor or mediation. If mediation is no longer necessary, please contact CORA Good Shepherd (<a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a>) and cite the reason for resolution, such as a successful rental assistance plan or tenant brought the rent current. If parties have documented the terms of their agreement, please submit the agreement to <a href="mailto:EvictionDiversionProgram@coraservices.org"><u>EvictionDiversionProgram@coraservices.org</u></a> so that the application can be closed with accurate outcomes.  
            </p>
          </li>
        </ol>
        <p>
          <b><u>Tenant Questions:</u></b>
          <ol>
            <li>
              <p><b>I am a tenant and I received a notice from the Eviction Diversion Program. What do I do next?</b></p>
              <p>
                If you received the Notice of Diversion Rights, please look for an email or text message from EDP about next steps. If you do not receive an email or text message within seven days, you should call the EDP Tenant Hotline at <a href="tel:215-523-9501"><u>215-523-9501</u></a> to find out your next steps 
              </p>
              <p>
                If you received an email or text message, please follow the instructions, which may include steps on how to access resources such a webinar session on direct negotiation with your landlord.
              </p>
              <p>
                If you have questions about your rights and responsibilities as a tenant outside of EDP, visit <a
                  target="_blank"
                  href="https://phillytenant.org"
                ><u>www.phillytenant.org</u></a> or call the Philly Tenant Hotline at <a href="tel:267-443-2500"><u>267-443-2500</u></a> and press 2. 
              </p>
            </li>
            <li>
              <p><b>What is the Tenant Intake Form and when should I complete it?</b></p>
              <p>
                After an EDP application is approved, tenants will receive a notification with a link to complete the Tenant Intake Form. Tenants are strongly encouraged to complete the intake form within five days. Tenants who do not complete the intake form within this time period risk ineligibility for program services. For example, a tenant on a TFA pathway will not be able to confirm eligibility for TFA until the tenant form is completed.
              </p>
            </li>
            <br>
            <li>
              <p><b>Can a tenant apply for the Eviction Diversion Program?</b></p>
              <p>
                No. Tenants cannot apply for the Eviction Diversion Program at this time. Tenants may seek a voluntary mediation through CORA Good Shepherd Mediation (CGSM) by calling <a href="tel:215-843-5413"><u>215-843-5413</u></a>, extension #2. (Please do not choose extension #1.) CGSM remains committed to equal access to mediation resources as part of its mission. Voluntary mediation through CGSM does not fulfill a landlord's obligation, when applicable, to participate in EDP.
              </p>
            </li>
            <br>
            <li>
              <p><b>My landlord has applied to Targeted Financial Assistance. As a tenant, what documents do I need to submit?</b></p>
              <p>
                If eligible for TFA, you will be asked to electronically sign the <a
                  target="_blank"
                  :href="$tenantAffidavit"
                ><u>Tenant Affidavit</u></a> as you complete the Tenant Form. You will receive a link to complete the Tenant form after your landlord's application to EDP has been approved.
              </p>
              <p>
                After you submit the Tenant Affidavit you may be asked to provide proof of identity and address (e.g., image of ID, utility bill, public benefits statement or paystub). 
              </p>
            </li>
            <li>
              <p><b>I have a scheduled Landlord-Tenant court date at Municipal Court. Do I need to attend if I am still working with my landlord through EDP?</b></p>
              <p>
                If you have a court date you MUST attend, regardless of what is happening through EDP. Mediation is NOT a hearing date. 
              </p>
            </li>
            <br>
            <li>
              <p><b>Where can I find out more about resources for tenants and tenant help?</b></p>
              <p>
                If you have questions about your rights and responsibilities as a tenant outside of EDP visit <a
                  target="_blank"
                  href="https://www.phillytenant.org"
                ><u>www.phillytenant.org</u></a> or call the Philly Tenant Hotline at <a href="tel:267-443-2500"><u>267-443-2500</u></a> and press 2. 
              </p>
              <p>
                For questions about EDP, call the EDP Tenant Hotline at <a href="tel:215-523-9501"><u>215-523-9501</u></a>. 
              </p>
            </li>
            <br>
            <li>
              <p><b>I am a tenant who lives in public housing that is owned and operated by PHA. Do I go through this program?</b></p>
              <p>
                Based on an agreement with the City of Philadelphia, the Philadelphia Housing Authority has a separate process for participating in the Eviction Diversion Program. For more information, please visit <a
                  target="_blank"
                  href="https://phillytenant.org/eviction-diversion-program/#i-live-in-pha-housing-can-i-participate-in-this-program"
                ><u>https://phillytenant.org/eviction-diversion-program/#i-live-in-pha-housing-can-i-participate-in-this-program</u></a>. 
              </p>
              <p>
                This does NOT apply to tenants who have a Housing Choice Voucher, also known as Section 8, or who live in subsidized housing or public housing not operated by PHA.
              </p>
            </li>
          </ol>
        </p>
      </div>
    </div>
    <br>
  </div>
</template>

<script>
export default {
  name: "FAQ",
};
</script>

<style>
.faq-line {
margin:0in;
font-weight: bold;
}
.faq-line-indent {
margin:0in;
margin-left:.5in;
margin-bottom: 5px;
}
.faq-list{
  margin-left:100px !important
}
.last{
  margin-bottom: 50px;
}
ol li::marker {
      font-weight: bold;
    }
ol li ol li::marker {
      font-weight: normal;
    }
</style>
